import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/work-with-me", label: "Work With Me" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "bg-white/90 backdrop-blur-md border-b border-borderwarm"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" data-testid="nav-logo" className="leading-none">
            <span className="font-serif text-2xl md:text-[26px] tracking-tight text-ink">
              Rooted Strategy
            </span>
            <span className="block font-sans text-[10px] tracking-[0.32em] uppercase text-rosedeep mt-1">
              with Roisin Doyle
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-sans text-[13px] tracking-wide hover-underline ${
                    isActive ? "text-ink" : "text-mutedfg hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/work-with-me"
              data-testid="nav-cta"
              className="ml-2 inline-flex items-center rounded-full bg-rosedeep text-white px-6 py-3 font-sans text-[12px] tracking-[0.15em] uppercase transition-colors duration-300 hover:bg-ink"
            >
              Work With Me
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-ink p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-borderwarm"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-serif text-3xl ${isActive ? "text-ink" : "text-mutedfg"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/work-with-me"
                data-testid="mobile-nav-cta"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-rosedeep text-white px-6 py-4 font-sans text-[12px] tracking-[0.15em] uppercase"
              >
                Work With Me
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
